import { AsyncPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { GoogleMapsModule } from '@angular/google-maps';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ConsentService, WindowRef } from '@boels/helpers';
import { BoelsPointOfService, GeoPoint } from '@boels/models';
import {
  faPlay,
  faSquareArrowUpRight,
} from '@fortawesome/free-solid-svg-icons';
import { TranslocoModule } from '@jsverse/transloco';
import { Observable } from 'rxjs';

export interface BoelsMapMarker {
  iconUrl: string;
}

@Component({
  selector: 'boels-google-maps',
  templateUrl: './boels-google-maps.component.html',
  styleUrls: ['./boels-google-maps.component.scss'],
  imports: [
    GoogleMapsModule,
    AsyncPipe,
    MatButtonModule,
    MatIconModule,
    MatBadgeModule,
    TranslocoModule,
  ],
})
export class BoelsGoogleMapsComponent implements OnChanges {
  private winRef = inject(WindowRef);
  private consentService = inject(ConsentService);
  private destroyRef = inject(DestroyRef);

  isBrowser = this.winRef.isBrowser();

  // active marker
  readonly sourceMarker: BoelsMapMarker = {
    iconUrl: '/assets/images/icons/google-maps/SourceMarker.svg',
  };

  // active branch marker
  readonly greyMarker: BoelsMapMarker = {
    iconUrl: '/assets/images/icons/google-maps/Greymarker_icon.svg',
  };

  // boels branch
  readonly orangeMarker: BoelsMapMarker = {
    iconUrl: '/assets/images/icons/google-maps/Orangemarker_icon.svg',
  };

  // cramo branch
  readonly redMarker: BoelsMapMarker = {
    iconUrl: '/assets/images/icons/google-maps/Redmarker_icon.svg',
  };

  faPlay = faPlay;
  faArrow = faSquareArrowUpRight;

  @Input()
  pointOfServices: (BoelsPointOfService & {
    label?: google.maps.MarkerLabel;
  })[];

  @Input()
  active: BoelsPointOfService;

  @Input()
  zoomLevel: number;

  @Input()
  fitBounds = false;

  @Input()
  mapCenter: GeoPoint;

  @Input()
  sourceMarkerPosition: GeoPoint;

  @Output()
  markerClicked: EventEmitter<BoelsPointOfService> =
    new EventEmitter<BoelsPointOfService>();

  private _map: google.maps.Map | undefined;
  private timeoutId: NodeJS.Timeout | undefined;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (!this.timeoutId) return;
      clearTimeout(this.timeoutId);
    });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pointOfServices']) {
      if (this.winRef.isBrowser()) {
        this.prepareLabels();
        this.active = this.pointOfServices[0];
        this.mapCenter = this.active.geoPoint;
      }
    }
  }
  cookieAccepted$(): Observable<boolean> {
    return this.consentService.hasConsentFor$('preferences');
  }

  prepareLabels(): void {
    this.pointOfServices = this.pointOfServices.map((pointOfService, index) => {
      const label: google.maps.MarkerLabel = {
        color: 'white',
        text: (index + 1).toString(),
        fontSize: '16px',
        fontWeight: 'bold',
        fontFamily: 'monospace',
        className: 'google-maps-marker',
      };
      return { ...pointOfService, label };
    });
  }

  clickMarker(boelsPointOfService: BoelsPointOfService): void {
    this.markerClicked.emit(boelsPointOfService);
  }

  mapLoaded(map: google.maps.Map): void {
    this._map = map;

    // set initial zoom level to 12 if the zoomlevel is higher
    const zoom = this._map.getZoom();

    if (zoom && zoom > 12) {
      this._map.setZoom(12);
    }

    // Handle the bounds of the map if set to true
    if (this.fitBounds) {
      this.fitMapToBounds();
    }
  }
  fitMapToBounds(): void {
    try {
      const bounds = new google.maps.LatLngBounds();
      this.pointOfServices.forEach((pointOfService) => {
        if (pointOfService.geoPoint) {
          bounds.extend(
            new google.maps.LatLng(
              pointOfService.geoPoint.latitude,
              pointOfService.geoPoint.longitude,
            ),
          );
        }
      });
      if (this._map && bounds) {
        this._map.fitBounds(bounds);

        // set the zoom level to 12 if the zoom level is higher after doing fitbounds
        const zoomChangeBoundsListener = google.maps.event.addListenerOnce(
          this._map,
          'bounds_changed',
          () => {
            if (this._map) {
              // set the zoom level to 12 if the zoom level is higher
              const zoom = this._map.getZoom();
              if (zoom && zoom > 12) {
                this._map.setZoom(12);
              }
            }
          },
        );
        // Remove the listener
        this.timeoutId = setTimeout(function () {
          google.maps.event.removeListener(zoomChangeBoundsListener);
        }, 2000);
      }
    } catch (error) {
      console.error('Error creating LatLngBounds:', error);
    }
  }
}
